import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Redirect, useHistory } from 'react-router';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useEffect } from 'react';
import { apiUrl, stripePublicKey } from '@dashboard/services/env';
import request, { fetcher } from '@dashboard/services/request';
import { Formik } from 'formik';
import { FormElement, Label } from '@dashboard/components/Form';
import { FormSubmit, Select, TextInput } from '@dashboard/components/Formik';
import { countries, isEU } from '@dashboard/services/countries/countries';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Spinner from '@dashboard/components/Spinner';
import { getPlan } from '@dashboard/services/plans';
import { formatPrice } from '@dashboard/services/formatting';
import { useToast } from '@dashboard/context/ToastContext';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import { sellerBillingAddress, sellerKey } from '@dashboard/lib/queryKeys';
import getCouponDiscount from './getCouponDiscount';
import { stringify } from 'query-string';
import useRewardful from '@dashboard/hooks/useRewardful';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { isEmpty } from 'lodash';
import { tracking } from '../../../../lib/tracking';

const Upgrade = () => {
  const { plan: planHandle } = useUrlQuery();

  const plan = getPlan(planHandle);

  if (!plan) {
    return <Redirect to="/billing/plan" />;
  }

  return <UpgradePlanSteps />;
};

function PriceBreakdown({ plan, coupon, billingPeriod }) {
  const monthlyCouponDiscount = getCouponDiscount(plan?.monthlyPrice, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const yearlyCouponDiscount = getCouponDiscount(plan?.yearlyPrice * 12, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const discountedMonthlyPrice = plan?.monthlyPrice - monthlyCouponDiscount;

  const initialYearlyPrice = plan?.monthlyPrice * 12;
  const yearlyPrice = plan?.yearlyPrice * 12;
  const discountedYearlyPrice = yearlyPrice - yearlyCouponDiscount;

  return (
    <div className="mb-4">
      {billingPeriod === 'year' && (
        <>
          <h3 className="text-lg mt-2 font-normal mb-4">
            <span className="line-through text-gray-600 mr-2">
              {formatPrice({
                amount: initialYearlyPrice,
                currency: 'USD',
              })}{' '}
              / year
            </span>
            {formatPrice({
              amount: yearlyPrice,
              currency: 'USD',
            })}{' '}
            / year
          </h3>

          {coupon && (
            <>
              <div className="bg-orange-50 px-4 py-4 space-y-1 text-right rounded inline-block">
                <p>
                  <span className="font-semibold">Discount</span>:{' '}
                  {formatPrice({
                    amount: yearlyCouponDiscount,
                    currency: 'USD',
                  })}
                  <>
                    {coupon.duration === 'once' && ' (one-time)'}

                    {coupon.duration === 'forever' && ' / year'}

                    {coupon.duration === 'repeating' && ` (${coupon.duration_in_months} months)`}
                  </>
                </p>

                <p>
                  <span className="font-semibold">Total</span>:{' '}
                  {formatPrice({
                    amount: discountedYearlyPrice,
                    currency: 'USD',
                  })}{' '}
                  / year
                </p>
              </div>
            </>
          )}
        </>
      )}

      {billingPeriod === 'month' && (
        <>
          <h3 className="text-lg mt-2 font-normal mb-4">
            {formatPrice({ amount: plan.monthlyPrice, currency: 'USD' })} / month
          </h3>

          {coupon && (
            <div className="bg-orange-50 px-4 py-4 space-y-1 text-right rounded inline-block">
              <p>
                <span className="font-semibold">Discount</span>:{' '}
                {formatPrice({
                  amount: monthlyCouponDiscount,
                  currency: 'USD',
                })}
                <>
                  {coupon.duration === 'once' && ' (one-time)'}

                  {coupon.duration === 'forever' && ' / month'}

                  {coupon.duration === 'repeating' && ` (${coupon.duration_in_months} months)`}
                </>
              </p>

              <p>
                <span className="font-semibold">Total</span>:{' '}
                {formatPrice({
                  amount: discountedMonthlyPrice,
                  currency: 'USD',
                })}{' '}
                / month
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const UpgradePlanSteps = () => {
  const { sellerId, token } = useAuth();
  const {
    plan: planHandle,
    billing_period: billingPeriod,
    coupon: couponQuery,
    upgradeFlow,
  } = useUrlQuery();

  const upgradePlan = getPlan(planHandle);

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  return (
    <SettingsContainer>
      <SettingsHeader>{upgradePlan.title}</SettingsHeader>

      <PriceBreakdown plan={upgradePlan} coupon={coupon} billingPeriod={billingPeriod} />

      <p className="text-black mb-4">Complete your details below to start your subscription</p>

      <UpgradeForm
        onSuccessPath="/billing/plan"
        planHandle={planHandle}
        billingPeriod={billingPeriod}
        couponQuery={couponQuery}
        upgradeFlow={upgradeFlow}
      />
    </SettingsContainer>
  );
};

export const UpgradeForm = ({
  onSuccessPath,
  planHandle,
  billingPeriod,
  couponQuery,
  upgradeFlow,
}) => {
  const { sellerId, isAuthenticating, token, setSeller } = useAuth();
  const { emitToast } = useToast();
  const { getSellerAddress } = useSellerQueries();
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);

  const [shouldPollSeller, setShouldPollSeller] = useState(false);

  const upgradePlan = getPlan(planHandle);

  const { coupon: rewardfulCoupon, referral: rewardfulReferral } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  const { data: address, isLoading } = useQuery({
    queryKey: sellerBillingAddress(sellerId),
    queryFn: () => getSellerAddress(sellerId),
    enabled: !!sellerId,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const { data: updatedSeller } = useQuery({
    queryKey: sellerKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
    enabled: shouldPollSeller,
    refetchInterval: 1000,
  });

  useEffect(() => {
    if (
      updatedSeller?.stripeSubscriptionId &&
      updatedSeller?.plan?.handle === upgradePlan.handle &&
      !updatedSeller?.stripeSubscriptionCancelAt
    ) {
      setSeller(updatedSeller);

      emitToast({
        title: "You're subscribed!",
        description: `Your subscription has been updated to the ${upgradePlan.title} plan`,
        type: 'success',
      });

      tracking.trackEvent('Subscribe', {
        currency: 'USD',
        predicted_ltv: 800,
        subscriptionId: updatedSeller.stripeSubscriptionId,
        plan: updatedSeller.plan?._id,
      });

      history.push(onSuccessPath);
    }
  }, [updatedSeller]);

  if (isAuthenticating) {
    return null;
  }

  async function confirmSubscriptionPayment(clientSecret) {
    const { error } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    });

    if (error) {
      throw error;
    }
  }

  async function confirmSubscriptionSetup(setupIntentClientSecret) {
    const { error } = await stripe.confirmCardSetup(setupIntentClientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    });

    if (error) {
      throw error;
    }
  }

  // check if the address country is in the countries array
  // otherwise format may be incorrect, so we let user fix in the address form
  const adressCountry = countries.find(country => country.alpha2 === address?.country);

  const isAddressIncomplete =
    !address ||
    !address?.firstName ||
    !address?.lastName ||
    !address?.streetAddress ||
    !address?.city ||
    !address?.postalCode ||
    !address?.country ||
    !adressCountry;

  return (
    <>
      {isLoading && (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <Formik
          enableReinitialize
          initialValues={{
            firstName: address?.firstName,
            lastName: address?.lastName,
            companyName: address?.companyName,
            streetAddress: address?.streetAddress,
            streetAddress2: address?.streetAddress2,
            city: address?.city,
            state: address?.state,
            postalCode: address?.postalCode,
            country: address?.country,
            vatNumber: address?.vatNumber,
          }}
          onSubmit={async (values, actions) => {
            actions.setStatus('');

            try {
              if (!clientSecret && !setupIntentClientSecret) {
                const result = await request(
                  apiUrl + `/sellers/${sellerId}/start-subscription`,
                  token,
                  {
                    body: JSON.stringify({
                      ...values,
                      plan: upgradePlan.handle,
                      billingPeriod: billingPeriod,
                      referral: rewardfulReferral,
                      ...(coupon && { coupon: coupon.id }),
                      upgradeFlow,
                    }),
                    method: 'POST',
                  },
                );

                if (!result.data) {
                  throw result;
                }

                if (result.data.clientSecret) {
                  setClientSecret(result.data.clientSecret);

                  await confirmSubscriptionPayment(result.data.clientSecret);
                }

                if (result.data.setupIntentClientSecret) {
                  setSetupIntentClientSecret(result.data.setupIntentClientSecret);

                  await confirmSubscriptionSetup(result.data.setupIntentClientSecret);
                }
              } else {
                if (clientSecret) {
                  await confirmSubscriptionPayment(clientSecret);
                }

                if (setupIntentClientSecret) {
                  await confirmSubscriptionSetup(setupIntentClientSecret);
                }
              }

              setShouldPollSeller(true);
            } catch (err) {
              const { status, errors } = transformApiErrorToFormik(err);

              if (!isEmpty(errors)) {
                actions.setErrors(errors);
              } else {
                actions.setStatus(status);
              }

              actions.setSubmitting(false);
            }
          }}
        >
          {({ values, status, handleSubmit }) => (
            <form onSubmit={handleSubmit} autoComplete>
              {isAddressIncomplete && (
                <>
                  <div style={{ display: 'flex' }}>
                    <FormElement style={{ marginRight: '1rem', flex: '1' }}>
                      <Label htmlFor="firstName">First name</Label>
                      <TextInput
                        name="firstName"
                        id="firstName"
                        type="text"
                        placeholder="First name"
                        required
                        border
                      />
                      <FormError name="firstName" />
                    </FormElement>

                    <FormElement style={{ flex: '1' }}>
                      <Label htmlFor="lastName">Last name</Label>
                      <TextInput
                        name="lastName"
                        id="lastName"
                        type="text"
                        placeholder="Last name"
                        required
                        border
                      />
                      <FormError name="lastName" />
                    </FormElement>
                  </div>

                  <FormElement>
                    <Label htmlFor="companyName" optional>
                      Company name
                    </Label>
                    <TextInput
                      name="companyName"
                      id="companyName"
                      type="text"
                      placeholder="Company name"
                      border
                    />
                    <FormError name="companyName" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor="streetAddress">Address line 1</Label>
                    <TextInput
                      name="streetAddress"
                      id="streetAddress"
                      type="text"
                      placeholder="Address line 1"
                      required
                      border
                    />
                    <FormError name="streetAddress" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor="streetAddress2" optional>
                      Address line 2
                    </Label>
                    <TextInput
                      name="streetAddress2"
                      id="streetAddress2"
                      type="text"
                      placeholder="Address line 2"
                      border
                    />
                    <FormError name="streetAddress2" />
                  </FormElement>

                  <div style={{ display: 'flex' }}>
                    <FormElement style={{ marginRight: '1rem', flex: '1' }}>
                      <Label htmlFor="country">Country</Label>

                      <Select name="country" id="country" border placeholder="Country" required>
                        <option>Select country</option>

                        {countries.map(country => (
                          <option value={country.alpha2} key={country.alpha2}>
                            {country.name}
                          </option>
                        ))}
                      </Select>
                      <FormError name="country" />
                    </FormElement>

                    <FormElement style={{ flex: '1' }}>
                      <Label htmlFor="city">City</Label>
                      <TextInput
                        name="city"
                        id="city"
                        type="text"
                        placeholder="City"
                        required
                        border
                      />
                      <FormError name="city" />
                    </FormElement>
                  </div>

                  <div style={{ display: 'flex' }}>
                    <FormElement style={{ marginRight: '1rem', flex: '1' }}>
                      <Label htmlFor="state" optional>
                        State
                      </Label>
                      <TextInput name="state" id="state" type="text" placeholder="State" border />
                      <FormError name="state" />
                    </FormElement>

                    <FormElement style={{ flex: '1' }}>
                      <Label htmlFor="postalCode">Postal code / Zip</Label>
                      <TextInput
                        name="postalCode"
                        id="postalCode"
                        type="text"
                        placeholder="Postal code / Zip"
                        required
                        border
                      />
                      <FormError name="postalCode" />
                    </FormElement>
                  </div>

                  {values.country && isEU(values.country) && (
                    <FormElement>
                      <Label htmlFor="vatNumber">VAT Number</Label>
                      <TextInput
                        name="vatNumber"
                        id="vatNumber"
                        type="text"
                        placeholder="VAT Number"
                        border
                      />
                      <FormError name="vatNumber" />
                    </FormElement>
                  )}
                </>
              )}

              <FormElement>
                <Label htmlFor="stripe-card-element">Card</Label>
                <CardElement
                  id="stripe-card-element"
                  options={{
                    style: {
                      base: {
                        iconColor: '#c0b6ba',
                        fontWeight: 400,
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
                        fontSmoothing: 'antialiased',
                        background: 'white',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        padding: '8px',
                        color: '#382b2f', // text-gray-800
                        '::placeholder': {
                          color: '#c0b6ba',
                        },
                        '::focus-visible': {
                          outline: 'none',
                        },
                      },
                    },
                  }}
                  className="bg-white placeholder-gray-200 border-none shadow-input focus:shadow-input-focus appearance-none block w-full py-2 px-2 rounded"
                />
              </FormElement>

              <FormErrorBase>{status && status.error && status.error}</FormErrorBase>

              <FormSubmit>
                <FormSubmit.Button appearance="primary" size="full" disabled={shouldPollSeller}>
                  Start subscription
                </FormSubmit.Button>
              </FormSubmit>
            </form>
          )}
        </Formik>
      )}
    </>
  );
};

const ElementsWrapper = ({ children }) => {
  return (
    <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
      {children}
    </Elements>
  );
};

export default function UpgradeContainer(props) {
  const {
    plan: planHandle,
    billing_period: billingPeriod,
    coupon: couponQuery,
    upgradeFlow,
  } = useUrlQuery();

  return (
    <ElementsWrapper>
      <Upgrade {...props} />

      <div className="mt-8">
        <Link
          to={`/billing/plan?${stringify({
            plan: planHandle,
            billing_period: billingPeriod,
            coupon: couponQuery,
            upgradeFlow,
          })}`}
          variant="secondary"
        >
          Back
        </Link>
      </div>
    </ElementsWrapper>
  );
}
